import { useState, useRef } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useToast } from "@/hooks/use-toast"
import { Textarea } from "@/components/ui/textarea"
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { 
  Camera, 
  Mail, 
  Upload, 
  Copy,
  Share,
  CheckCircle,
  MessageSquare,
  Loader2
} from "lucide-react"
import type { User } from "@shared/schema"
import { compressImage } from "@/lib/imageCompression"
import { useEdition } from "@/lib/edition"
import { VideoUploadPanel } from "@/components/VideoUploadPanel"
import { UploadBatchStatus } from "@/components/UploadBatchStatus"
import {
  runUploadQueue, validateImageFiles, summarizeResults, UploadSkipError,
  type FileResult, type QueueProgress, type UploadResponse
} from "@/lib/uploadQueue"

// Must match the server's multer fileFilter allowlist (server/routes/uploads.ts).
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

// One client upload id per picked File object (PLAN §14 C-5): a retry of the same file reuses it, so the
// server returns the photo it already stored. Forgotten after a successful upload.
const clientUploadIds = new WeakMap<File, string>()
function clientUploadIdFor(file: File): string {
  let id = clientUploadIds.get(file)
  if (!id) {
    id = typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
    clientUploadIds.set(file, id)
  }
  return id
}
function forgetClientUploadId(file: File) {
  clientUploadIds.delete(file)
}

interface ContentUploadModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: User
  /** Check-in step 4: the theme (content pillar) these photos belong to; the server keeps it only if it is a current pillar. */
  pillarName?: string
  /** Where the upload starts. "checkin": a hotel's check-in photos are only stored, never sent to the n8n photo
   *  workflow (Mike 2026-10-10, Variante A); the server decides. Foto-Bibliothek/Dashboard leave it unset. */
  origin?: "checkin"
}

export function ContentUploadModal({ open, onOpenChange, user, pillarName, origin }: ContentUploadModalProps) {
  const { toast } = useToast()
  const [isUploading, setIsUploading] = useState(false)
  const [isCompressing, setIsCompressing] = useState(false)
  const [progress, setProgress] = useState<QueueProgress | null>(null)
  const [batchResults, setBatchResults] = useState<FileResult[] | null>(null)
  const cancelRef = useRef<AbortController | null>(null)
  const [uploadMessage, setUploadMessage] = useState("")
  const [isDragging, setIsDragging] = useState(false)
  const [copiedType, setCopiedType] = useState<'email' | 'url' | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Hotel-Edition (PLAN §6.3): hotel texts by edition. The e-mail way runs through the external mail pipeline
  // (no app handler), so it goes away for a hotel only once the server stores its photos itself
  // (HOTEL_UPLOADS_DIRECT_ENABLED, resolved server-side, review he-c R4). Switch off = window unchanged.
  const editionState = useEdition()
  const isHotel = editionState.status === "ready" && editionState.edition.kind === "hotel"
  const capability = useQuery<{ hotelDirectUploads: boolean; emailUpload: boolean; hotelVideoUploads?: boolean; hotelVideoBatch?: boolean }>({
    queryKey: ["/api/upload/capabilities"],
    enabled: open && isHotel,
    staleTime: 60_000,
  })
  // Agencies: always. Hotels: only when the server says so (hidden while unknown, never shown by mistake).
  const showEmailUpload = !isHotel || capability.data?.emailUpload === true
  // Hotel video (PLAN.md §G): only when the server allows it, never in the check-in (Mike 2026-10-16).
  const showVideoUpload = isHotel && origin !== "checkin" && capability.data?.hotelVideoUploads === true
  const queryClient = useQueryClient()
  // Several videos at once (PLAN-multivideo.md §35): the switch is read again before every further file.
  const checkVideoBatch = async (signal: AbortSignal) => {
    const res = await fetch("/api/upload/capabilities", { credentials: "include", signal })
    const data = res.ok ? await res.json().catch(() => null) : null
    return data?.hotelVideoBatch === true
  }

  const uploadEmail = `photos-${user.uploadToken}@linkedin-lina.app`
  const uploadUrl = `${window.location.origin}/api/upload/${user.uploadToken}`

  const copyToClipboard = async (text: string, type: 'email' | 'url') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedType(type)
      setTimeout(() => setCopiedType(current => (current === type ? null : current)), 2000)
      toast({
        title: type === 'email' ? "E-Mail kopiert" : "Link kopiert",
        description: `${type === 'email' ? 'Die Upload-E-Mail' : 'Der Upload-Link'} wurde in die Zwischenablage kopiert.`
      })
    } catch (error) {
      toast({
        title: "Fehler beim Kopieren",
        description: "Bitte kopieren Sie die Adresse manuell.",
        variant: "destructive"
      })
    }
  }

  const handleDirectUpload = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || isUploading) return
    // Copy first: clearing the input empties the live FileList.
    const picked = Array.from(fileList)
    if (fileInputRef.current) fileInputRef.current.value = ''
    const { valid, skipped } = validateImageFiles(picked)

    if (valid.length === 0) {
      setBatchResults(skipped)
      toast({
        title: "Keine passende Datei",
        description: "Bitte wählen Sie Bilddateien bis 10 MB aus.",
        variant: "destructive"
      })
      return
    }

    setBatchResults(null)
    setIsUploading(true)
    const controller = new AbortController()
    cancelRef.current = controller
    let storedDirectly = false
    const message = uploadMessage.trim()

    const uploadOne = async (file: File, signal: AbortSignal): Promise<UploadResponse> => {
      setIsCompressing(true)
      let fileToUpload: File | Blob = file
      let fileName = file.name
      try {
        const result = await compressImage(file, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.8,
          maxSizeMB: 0.5
        })
        fileToUpload = result.blob
        fileName = file.name.replace(/\.[^/.]+$/, '.jpg')
      } catch {
        // Compression failed (the browser couldn't decode this format). Safari CAN decode HEIC, so iPhone
        // photos re-encode to JPG above; this branch only hits browsers that can't. If the ORIGINAL format
        // isn't one the server accepts, skip the file with a specific reason instead of sending a file the
        // server will reject with a generic 400.
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
          throw new UploadSkipError("Format nicht unterstützt (z. B. HEIC), bitte JPG oder PNG verwenden")
        }
        // Otherwise fall back to uploading the (server-acceptable) original.
      } finally {
        setIsCompressing(false)
      }

      const formData = new FormData()
      formData.append('photo', fileToUpload, fileName)
      if (pillarName) formData.append('pillarName', pillarName)
      if (message) formData.append('message', message)
      // Per-file id (PLAN §14 C-5): a retry of THIS file (also after a 429 pause) returns the same stored photo.
      formData.append('clientUploadId', clientUploadIdFor(file))
      if (origin) formData.append('origin', origin)

      // NOTE: fetch gives no upload-byte progress; the batch shows "Foto x von n" instead of a fake percentage.
      const response = await fetch('/api/upload/direct', { method: 'POST', body: formData, signal })
      if (response.ok) {
        forgetClientUploadId(file)
        const stored = await response.json().catch(() => null) as { status?: string } | null
        if (stored?.status === 'received') storedDirectly = true
      }
      return response
    }

    const results = await runUploadQueue(valid, { signal: controller.signal, onProgress: setProgress, upload: uploadOne })
    cancelRef.current = null

    const all = [...results, ...skipped]
    const okCount = results.filter(r => r.status === "ok").length
    const allOk = okCount === results.length && skipped.length === 0
    setProgress(null)
    setIsCompressing(false)
    setBatchResults(all)
    if (fileInputRef.current) fileInputRef.current.value = ''

    if (okCount === 0) {
      setIsUploading(false)
      toast({
        title: "Upload-Fehler",
        description: "Die Fotos konnten nicht hochgeladen werden.",
        variant: "destructive"
      })
      // Keep uploadMessage so the user can retry without re-typing their context.
      return
    }

    // One toast per batch, not one per photo.
    toast({
      title: allOk ? "Upload erfolgreich" : "Upload abgeschlossen",
      description: storedDirectly
        ? `${summarizeResults(all)}. Ihr Team sieht die Fotos jetzt in Ihrer Post-Bibliothek.`
        : `${summarizeResults(all)}. Die Fotos werden verarbeitet...`
    })
    // Failed or skipped files: keep the window open so the list stays readable and those files can be picked again.
    if (!allOk) {
      setIsUploading(false)
      return
    }

    setTimeout(() => {
      onOpenChange(false)
      setIsUploading(false)
      setUploadMessage("")
      setBatchResults(null)
      if (storedDirectly) return

      // Scroll to the pending section once it appears.
      const pollForUpload = setInterval(() => {
        const pendingSection = document.querySelector('[data-testid="pending-uploads-section"]')
        if (pendingSection) {
          clearInterval(pollForUpload)
          setTimeout(() => {
            pendingSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
            toast({
              title: "Fotos warten auf Freigabe",
              description: "Prüfen Sie Ihre hochgeladenen Fotos und geben Sie sie frei.",
            })
          }, 300)
        }
      }, 500)
      setTimeout(() => clearInterval(pollForUpload), 10000)
    }, storedDirectly ? 600 : 1000)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Only clear when the pointer actually leaves the zone — crossing onto a child node
    // would otherwise flicker the highlight off/on during the drag.
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setIsDragging(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      handleDirectUpload(files)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => {
      if (!next) {
        // Closing the window stops a running batch (photos already stored stay) and clears the old summary.
        cancelRef.current?.abort()
        setBatchResults(null)
      }
      onOpenChange(next)
    }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Camera className="h-6 w-6" />
            <span>{pillarName ? `Fotos zum Thema „${pillarName}“` : "Foto hochladen"}</span>
          </DialogTitle>
          <DialogDescription className="text-base">
            {isHotel
              ? "Laden Sie Fotos direkt hier hoch. Ihr Team sieht sie sofort und plant sie für Ihre Beiträge ein."
              : "Wählen Sie den besten Weg: vom Handy oder direkt hier"}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Two upload methods, co-equal: e-mail first on mobile, direct upload first on desktop */}
          <div className="flex flex-col gap-4">
          {/* E-Mail upload (hotels: only while the app does not store their photos itself, see above) */}
          {showEmailUpload && (
          <div className="order-1 sm:order-2">
          <Card data-testid="card-email-upload">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                <span>Vom Handy per E-Mail</span>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Perfekt für iPhone & Android - einfach Foto machen und verschicken
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-3 sm:p-4 bg-background rounded-lg border-2 border-primary/20">
                  <p className="text-xs sm:text-sm font-medium mb-2 text-muted-foreground">Ihre persönliche Upload-E-Mail:</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input 
                      value={uploadEmail}
                      readOnly 
                      className="flex-1 font-mono text-xs sm:text-sm bg-white dark:bg-gray-900"
                      data-testid="input-upload-email"
                    />
                    <Button
                      onClick={() => copyToClipboard(uploadEmail, 'email')}
                      data-testid="button-copy-email"
                      className="w-full sm:w-auto min-h-[44px]"
                    >
                      {copiedType === 'email' ? (
                        <><CheckCircle className="h-4 w-4 mr-2" aria-hidden="true" />Kopiert!</>
                      ) : (
                        <><Copy className="h-4 w-4 mr-2" aria-hidden="true" />Kopieren</>
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-lg p-3 sm:p-4">
                  <h5 className="font-medium mb-2 flex items-center gap-2">
                    <span className="text-primary">📱</span>
                    So geht's in 3 Schritten:
                  </h5>
                  <ol className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-primary min-w-[20px]">1.</span>
                      <span>Foto mit dem Handy machen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-primary min-w-[20px]">2.</span>
                      <span>Auf "Teilen" tippen und "E-Mail" wählen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-primary min-w-[20px]">3.</span>
                      <span>An Ihre Upload-E-Mail senden, fertig!</span>
                    </li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
          )}

          {/* Direkter Upload */}
          <div className="order-2 sm:order-1">
          <Card data-testid="card-direct-upload">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Upload className="h-5 w-5" />
                <span>Direkter Upload</span>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Sofort vom Computer oder direkt Kamera öffnen
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Message Field */}
                <div className="space-y-2">
                  <label htmlFor="upload-message" className="text-sm font-medium flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" aria-hidden="true" />
                    <span>{isHotel ? "Nachricht an Ihr Team (optional)" : "Nachricht (optional)"}</span>
                  </label>
                  <p className="text-xs text-muted-foreground -mt-1">
                    {isHotel ? "Zum Beispiel: Wofür ist das Foto gedacht, wann wurde es aufgenommen?" : "Wird zusammen mit dem Foto gesendet."}
                  </p>
                  <Textarea
                    id="upload-message"
                    placeholder={isHotel ? "Ihre Nachricht an Ihr Team" : "Beschreiben Sie Ihr Foto oder ergänzen Sie Kontext"}
                    value={uploadMessage}
                    onChange={(e) => setUploadMessage(e.target.value)}
                    className="min-h-[60px] resize-none"
                    data-testid="textarea-upload-message"
                  />
                </div>

                {/* Upload Zone */}
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Fotos auswählen oder aufnehmen"
                  className={`border-2 border-dashed rounded-lg p-6 sm:p-8 text-center transition-colors hover-elevate cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${isDragging ? 'border-primary bg-primary/5' : 'border-primary/30'}`}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      fileInputRef.current?.click()
                    }
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  data-testid="upload-zone"
                >
                  <input
                    ref={fileInputRef}
                    id="photo-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleDirectUpload(e.target.files)}
                    data-testid="input-photo-upload"
                  />
                  <div className="space-y-3">
                    <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Camera className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-base sm:text-lg font-medium">Fotos auswählen oder aufnehmen</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Mobile: Fotos aus der Galerie wählen oder neu aufnehmen
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Desktop: Dateien ziehen oder klicken (mehrere möglich, max. 10MB pro Foto)
                      </p>
                    </div>
                  </div>
                </div>

                {showVideoUpload && (
                  <VideoUploadPanel
                    basePath="/api/upload/video"
                    accessKey={`user:${user.id}`}
                    message={uploadMessage}
                    pillarName={pillarName}
                    batchEnabled={capability.data?.hotelVideoBatch === true}
                    batchAllowed={checkVideoBatch}
                    onDone={() => queryClient.invalidateQueries({ queryKey: ["/api/content/videos"] })}
                  />
                )}

                {/* Compression Progress */}
                {isCompressing && (
                  <div className="space-y-2" data-testid="compression-progress">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Bild wird optimiert...</span>
                    </div>
                  </div>
                )}

                <UploadBatchStatus
                  progress={progress}
                  results={batchResults}
                  onCancel={() => cancelRef.current?.abort()}
                />
              </div>
            </CardContent>
          </Card>
          </div>
          </div>

          {/* 3. Share-Link (Collapsible/Optional) */}
          <details className="group">
            <summary className="cursor-pointer list-none">
              <Card className="hover-elevate transition-all" data-testid="card-share-links">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Share className="h-4 w-4" />
                      <span>Weitere Optionen</span>
                    </CardTitle>
                    <span className="text-xs text-muted-foreground group-open:hidden">▼</span>
                    <span className="text-xs text-muted-foreground hidden group-open:inline">▲</span>
                  </div>
                </CardHeader>
              </Card>
            </summary>
            <Card className="mt-2" data-testid="card-share-links-content">
              <CardContent className="pt-4">
                <div className="space-y-3">
                  <p className="text-sm font-medium">Upload-Link teilen</p>
                  <p className="text-xs text-muted-foreground">
                    Teilen Sie diesen Link mit Ihrem Team, damit es Ihnen Fotos senden kann
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input 
                      value={uploadUrl}
                      readOnly 
                      className="flex-1 font-mono text-xs sm:text-sm"
                      data-testid="input-upload-url"
                    />
                    <Button
                      onClick={() => copyToClipboard(uploadUrl, 'url')}
                      data-testid="button-copy-url"
                      className="w-full sm:w-auto min-h-[44px]"
                    >
                      {copiedType === 'url' ? (
                        <><CheckCircle className="h-4 w-4 mr-2" aria-hidden="true" />Kopiert!</>
                      ) : (
                        <><Copy className="h-4 w-4 mr-2" aria-hidden="true" />Link kopieren</>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </details>
        </div>
      </DialogContent>
    </Dialog>
  )
}